import React, { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  getDoc,
} from "firebase/firestore";

import { startOfWeek, addDays, format } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import logoImg from "../../assets/logo.png";
import { Link } from "react-router-dom";

export default function RotaEditor() {
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState({ start: "", end: "" });
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const [activeCard, setActiveCard] = useState(0);
  const carouselRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type }), 3000);
  };

  async function getLatLngFromPostcode(postcode) {
    try {
      const response = await fetch(
        `https://api.postcodes.io/postcodes/${postcode}`
      );
      const data = await response.json();
      if (data.status === 200 && data.result) {
        return {
          lat: data.result.latitude,
          lng: data.result.longitude,
        };
      } else {
        console.warn("❌ Invalid postcode:", postcode);
        return null;
      }
    } catch (error) {
      console.error("❌ Postcode lookup failed:", error);
      return null;
    }
  }

  // 🔹 Load clients & staff
  useEffect(() => {
    const loadData = async () => {
      const clientsSnap = await getDocs(collection(db, "clients"));
      const staffSnap = await getDocs(collection(db, "staff"));
      setClients(clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setStaff(staffSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    loadData();
  }, []);



// ──────────────────────────────────────────────
// Generate Weekly Rota (idempotent, no duplicates in /shifts)
// ──────────────────────────────────────────────
const generateWeeklyRota = async () => {
  if (loading) return;
  setLoading(true);

  const weekStart = startOfWeek(new Date(selectedWeek), { weekStartsOn: 1 });
  const weekEnd = addDays(weekStart, 6);
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const newAssignments = [];

  for (const client of clients) {
    let workDays = [];

    // Normalize daysOfWork to an array
    if (Array.isArray(client.daysOfWork)) {
      workDays = client.daysOfWork.map((d) => d.trim());
    } else if (typeof client.daysOfWork === "string") {
      workDays = client.daysOfWork.split(",").map((d) => d.trim());
    }

    // 🧭 Get client coordinates from postcode
    let clientLocation = null;
    if (client.postcode) {
      clientLocation = await getLatLngFromPostcode(client.postcode);
    }

    // Loop through each required work day
    for (const day of workDays) {
      const staffMatch = staff.find((s) => {
        const available = Array.isArray(s.daysAvailable)
          ? s.daysAvailable
          : JSON.parse(s.daysAvailable || "[]");
        return available.includes(day);
      });

      if (!staffMatch) continue;

      // Extract shift times
      const rawShift = client.timePeriods?.[0] || "";
      let startTime = "";
      let endTime = "";

      if (rawShift && rawShift.includes("-")) {
        const [startRaw, endRaw] = rawShift.split("-").map((t) => t.trim());
        startTime = startRaw || "";
        endTime = endRaw || "";
      }

      // Fallback default hours if client has no timePeriods
      if (!startTime) startTime = "09:00";
      if (!endTime) endTime = "17:00";

      // Date string for this work day
      const dateStr = format(
        addDays(weekStart, days.indexOf(day)),
        "yyyy-MM-dd"
      );

      newAssignments.push({
        clientId: client.id,
        clientName: client.fullName,
        staffId: staffMatch.id,
        staffName: staffMatch.fullName,
        date: dateStr,
        startTime,
        endTime,
        totalHours: client.totalHours || 0,
        rate: client.rate || 15,
        clientLocation,
      });
    }
  }

  // Save rota doc for the week (history)
  await setDoc(doc(db, "rotas", `week_${format(weekStart, "yyyy-MM-dd")}`), {
    weekStart: format(weekStart, "yyyy-MM-dd"),
    weekEnd: format(weekEnd, "yyyy-MM-dd"),
    assignments: newAssignments,
    createdAt: new Date(),
  });

  // Sync generated shifts to /shifts collection
  for (const a of newAssignments) {
    // Stable ID (NOT using time)
    const shiftId = `${a.staffId}_${a.date}_${a.clientId}`;
    const ref = doc(db, "shifts", shiftId);

    // Avoid overwriting existing shifts
    const snap = await getDoc(ref);
    if (snap.exists()) continue;

    await setDoc(ref, {
      id: shiftId,
      clientId: a.clientId,
      staffId: a.staffId,
      client: a.clientName,
      staff: a.staffName,
      date: a.date,
      startTime: a.startTime,
      endTime: a.endTime,
      totalHours: a.totalHours,
      rate: a.rate,
      notes: "Domestic Support Service",
      status: "Assigned",
      clientLocation: a.clientLocation || null,
      createdAt: new Date(),
    });
  }

  setAssignments(newAssignments);
  setFilteredAssignments(newAssignments);
  setLoading(false);
  showToast("Weekly rota generated and saved!");
};
































  // ──────────────────────────────────────────────
  // View Latest Rota
  // ──────────────────────────────────────────────
  const viewLatestRota = async () => {
    setLoading(true);
    try {
      const rotasSnap = await getDocs(collection(db, "rotas"));
      if (rotasSnap.empty) {
        showToast("No rotas found in Firestore.", "error");
        setLoading(false);
        return;
      }

      // Sort by createdAt desc
      const allRotas = rotasSnap.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort(
          (a, b) =>
            new Date(b.createdAt?.seconds * 1000 || 0) -
            new Date(a.createdAt?.seconds * 1000 || 0)
        );

      const latestRota = allRotas[0];
      if (!latestRota?.assignments?.length) {
        showToast("Latest rota has no assignments.", "error");
        setLoading(false);
        return;
      }

      setAssignments(latestRota.assignments);
      setFilteredAssignments(latestRota.assignments);
      showToast(`Loaded rota for week starting ${latestRota.weekStart}`);
    } catch (err) {
      console.error("Error loading latest rota:", err);
      showToast("Failed to load rota. Check console for details.", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Load all rotas and flatten (initial view)
  useEffect(() => {
    const loadAllRotas = async () => {
      const rotasSnap = await getDocs(collection(db, "rotas"));
      let allAssignments = [];
      rotasSnap.docs.forEach((r) => {
        const data = r.data();
        if (Array.isArray(data.assignments)) {
          allAssignments = [...allAssignments, ...data.assignments];
        }
      });
      // sort by date desc
      allAssignments.sort((a, b) => new Date(b.date) - new Date(a.date));
      setAssignments(allAssignments);
      setFilteredAssignments(allAssignments);
    };
    loadAllRotas();
  }, []);

  // 🔹 Filter by selected week/month
  const handleRangeFilter = () => {
    if (!selectedRange.start || !selectedRange.end) {
      setFilteredAssignments(assignments);
      return;
    }
    const startDate = new Date(selectedRange.start);
    const endDate = new Date(selectedRange.end);
    const filtered = assignments.filter((a) => {
      const date = new Date(a.date);
      return date >= startDate && date <= endDate;
    });
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    setFilteredAssignments(filtered);
  };

  const getStaffColor = (name) => {
    if (!name) return "#f43f5e";
    const colors = [
      "#f43f5e",
      "#3b82f6",
      "#10b981",
      "#f59e0b",
      "#8b5cf6",
      "#ec4899",
      "#14b8a6",
      "#84cc16",
      "#06b6d4",
      "#f97316",
      "#22c55e",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // ──────────────────────────────────────────────
  // Export to PDF with logo header
  // ──────────────────────────────────────────────
  const exportRotaToPDF = async () => {
    const input = document.getElementById("rota-grid");
    if (!input) {
      alert("No rota grid found to export!");
      return;
    }

    const originalOverflow = input.style.overflow;
    input.style.overflow = "visible";
    input.style.height = "auto";

    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Header with Logo & Branding
    const logo = new Image();
    logo.src = logoImg;
    const headerY = 30;
    pdf.addImage(logo, "PNG", 40, headerY, 50, 50);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Convalesce Care & Recruitment", 100, headerY + 20);
    pdf.setFontSize(10);
    pdf.text("Professional & Expert Touch", 100, headerY + 35);

    pdf.setDrawColor(200);
    pdf.line(40, headerY + 60, pageWidth - 40, headerY + 60);

    pdf.setFontSize(12);
    pdf.text(
      `Weekly Rota — Generated: ${format(new Date(), "PPP")}`,
      40,
      headerY + 80
    );

    const canvas = await html2canvas(input, {
      scale: 4,
      scrollY: -window.scrollY,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");

    const imgWidth = pageWidth - 80;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = headerY + 100;

    pdf.addImage(imgData, "PNG", 40, position, imgWidth, imgHeight);
    heightLeft -= pdf.internal.pageSize.getHeight() - position;

    while (heightLeft > 0) {
      pdf.addPage();
      position = 40;
      pdf.addImage(imgData, "PNG", 40, position - heightLeft, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight() - 40;
    }

    input.style.overflow = originalOverflow;
    pdf.save(`Convalesce_Rota_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  // ──────────────────────────────────────────────
  // Carousel scroll handler (snap-to-center)
  // ──────────────────────────────────────────────
  const handleCarouselScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const width = e.target.offsetWidth || 1;
    const index = Math.round(scrollLeft / width);
    setActiveCard(index);
  };

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

  // Common card style for top carousel
  const topCardStyle = {
    backgroundColor: "white",
    borderRadius: "20px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
    padding: "20px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "150px",
  };

  return (
    <div
      style={{
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
        paddingTop: "90px",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          backgroundColor: "white",
          borderRadius: "20px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
          padding: "20px",
          paddingTop: "26px",
          color: "#374151",
          boxSizing: "border-box",
        }}
      >
        <h1
          style={{
            textAlign: "center",
            color: "gray",
            backgroundColor: "white",
            padding: "12px 22px",
            marginBottom: "20px",
            borderRadius: "20px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            width: "fit-content",
            fontSize: "16px",
            fontWeight: "600",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Rota Creator & Editor
        </h1>

        {/* ─────────────── Top Action Carousel (View / Generate) ─────────────── */}
        <div
          style={{
            width: "100%",
            marginBottom: "10px",
          }}
        >
          <div
            ref={carouselRef}
            onScroll={handleCarouselScroll}
            style={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              gap: "16px",
              padding: "10px 0 14px 0",
              margin: "0 -20px",
              paddingLeft: "20px",
              paddingRight: "20px",
              boxSizing: "border-box",
            }}
          >
            {/* View Rota Card */}
            <div
              onClick={!loading ? viewLatestRota : undefined}
              style={{
                ...topCardStyle,
                flex: "0 0 80%",
                maxWidth: "360px",
                minWidth: "260px",
                scrollSnapAlign: "center",
                margin: "0 auto",
                opacity: loading ? 0.6 : 1,
                cursor: loading ? "default" : "pointer",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "6px",
                  color: "#111827",
                }}
              >
                View Rota
              </h3>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  maxWidth: "240px",
                }}
              >
                {loading
                  ? "Loading latest rota..."
                  : "View the most recent rota saved in Firestore."}
              </p>
            </div>

            {/* Generate Rota Card */}
            <div
              onClick={!loading ? generateWeeklyRota : undefined}
              style={{
                ...topCardStyle,
                flex: "0 0 80%",
                maxWidth: "360px",
                minWidth: "260px",
                scrollSnapAlign: "center",
                margin: "0 auto",
                opacity: loading ? 0.6 : 1,
                cursor: loading ? "default" : "pointer",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "6px",
                  color: "#111827",
                }}
              >
                Generate Rota
              </h3>
              <p
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  maxWidth: "240px",
                }}
              >
                {loading
                  ? "Generating rota..."
                  : "Automatically create and save a rota for the selected week."}
              </p>
            </div>
          </div>

          {/* Carousel dots */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "6px",
              marginBottom: "10px",
            }}
          >
            {[0, 1].map((i) => (
              <span
                key={i}
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "999px",
                  backgroundColor:
                    activeCard === i ? "#1e3a8a" : "#d1d5db",
                  transition: "background-color 0.2s ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Week Picker for Generate Rota */}
        <div
          style={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
          }}
        >
          <span style={{ fontSize: "14px", color: "gray" }}>
            📅 Select week to generate:
          </span>
          <input
            type="date"
            value={format(new Date(selectedWeek), "yyyy-MM-dd")}
            onChange={(e) => setSelectedWeek(new Date(e.target.value))}
            style={{
              padding: "6px 10px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
              minWidth: "160px",
            }}
          />
        </div>

        {/* 🔹 Date Range Filter */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            backgroundColor: "white",
            borderRadius: "20px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            alignItems: "center",
            gap: "10px",
            marginBottom: "20px",
            padding: "10px",
            boxSizing: "border-box",
          }}
        >
          <label style={{ fontSize: "14px", color: "gray" }}>
            Select Start:
          </label>
          <input
            type="date"
            value={selectedRange.start}
            onChange={(e) =>
              setSelectedRange((prev) => ({ ...prev, start: e.target.value }))
            }
            style={{
              padding: "6px 10px",
              borderRadius: "8px",
              fontSize: "14px",
              border: "1px solid #d1d5db",
            }}
          />
          <label style={{ fontSize: "14px", color: "gray" }}>
            Select End:
          </label>
          <input
            type="date"
            value={selectedRange.end}
            onChange={(e) =>
              setSelectedRange((prev) => ({ ...prev, end: e.target.value }))
            }
            style={{
              padding: "6px 10px",
              borderRadius: "8px",
              fontSize: "14px",
              border: "1px solid #d1d5db",
            }}
          />
          <button
            onClick={handleRangeFilter}
            style={{
              backgroundColor: "#1e3a8a",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: "500",
              cursor: "pointer",
              boxShadow: "0 2px 5px rgba(30,58,138,0.3)",
            }}
          >
            Apply Filter
          </button>
          <button
            onClick={exportRotaToPDF}
            style={{
              backgroundColor: "#22c55e",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Export PDF
          </button>
        </div>

        {/* Manage Shifts Card */}
        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <Link
            to="/admin/shifts"
            className="cursor-pointer transition transform hover:scale-105 hover:shadow-xl"
            style={{
              display: "block",
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0 2px 5px #3b82f6",
              color: "gray",
              textDecoration: "none",
              padding: "16px 20px",
              textAlign: "center",
            }}
          >
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              Manage Shifts
            </h3>
            <p style={{ fontSize: "13px", color: "#6b7280" }}>
              View, edit, or delete existing shifts.
            </p>
          </Link>
        </div>

        {/* 🔹 Rota Info Line */}
        {filteredAssignments.length > 0 && (
          <h3
            style={{
              textAlign: "center",
              color: "gray",
              margin: "10px 0 10px 0",
              fontSize: "13px",
            }}
          >
            Showing rota for period ending{" "}
            {format(
              new Date(filteredAssignments[0].date),
              "dd MMM yyyy"
            )}
          </h3>
        )}

        {/* 🔹 Rota Table */}
        <div
          id="rota-grid"
          style={{
            overflowX: "auto",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            padding: "20px",
            marginBottom: "30px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead
              style={{ backgroundColor: "#f3f4f6", color: "#374151" }}
            >
              <tr>
                <th style={{ padding: "10px", textAlign: "center" }}>
                  Date
                </th>
                <th style={{ padding: "10px", textAlign: "center" }}>
                  Client
                </th>
                <th style={{ padding: "10px", textAlign: "center" }}>
                  Staff
                </th>
                <th style={{ padding: "10px", textAlign: "center" }}>
                  Shift
                </th>
                <th style={{ padding: "10px", textAlign: "center" }}>
                  Hours
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map((a, i) => (
                <tr
                  key={i}
                  style={{
                    borderBottom: "1px solid #e5e7eb",
                    backgroundColor:
                      i % 2 === 0 ? "#ffffff" : "#f9fafb",
                  }}
                >
                  <td
                    style={{ padding: "10px", textAlign: "center" }}
                  >
                    {a.date}
                  </td>
                  <td
                    style={{ padding: "10px", textAlign: "center" }}
                  >
                    {a.clientName}
                  </td>
                  <td
                    style={{
                      padding: "10px",
                      textAlign: "center",
                      color: getStaffColor(a.staffName),
                    }}
                  >
                    {a.staffName}
                  </td>
                  <td
                    style={{ padding: "10px", textAlign: "center" }}
                  >
                    {a.shift}
                  </td>
                  <td
                    style={{ padding: "10px", textAlign: "center" }}
                  >
                    {a.totalHours}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredAssignments.length === 0 && (
            <p
              style={{
                textAlign: "center",
                color: "gray",
                padding: "10px",
                fontSize: "13px",
              }}
            >
              No assignments found for the selected range.
            </p>
          )}
        </div>

        {/* ─────────────── Weekly Rota Table (Compact Interactive) ─────────────── */}
        <div
          style={{
            backgroundColor: "#111827",
            borderRadius: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            padding: "24px",
            marginTop: "30px",
          }}
          className="rounded-2xl p-6 shadow-md mt-6"
        >
          <h1
            style={{
              textAlign: "center",
              color: "#e5e7eb",
              fontSize: "15px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
            className="text-l font-semibold mb-6 text-center"
          >
            Weekly Rota
          </h1>

          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                tableLayout: "fixed",
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
                border: "1px solid #374151",
              }}
              className="min-w-[800px] w-full border-collapse text-center text-sm"
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#1f2937",
                    color: "#d1d5db",
                  }}
                  className="bg-gray-800 text-gray-300"
                >
                  <th
                    style={{ padding: "8px", fontSize: "11px" }}
                    className="p-2 border border-gray-700"
                  >
                    Time
                  </th>
                  {daysOfWeek.map((day, i) => (
                    <th
                      key={`day-${i}`}
                      style={{
                        color: "#d1d5db",
                        padding: "8px",
                        fontSize: "11px",
                      }}
                      className="p-2 border border-gray-700"
                    >
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {Array.from({ length: 16 }).map((_, hourIndex) => {
                  const hour = 7 + hourIndex;
                  const timeLabel = `${String(hour).padStart(
                    2,
                    "0"
                  )}:00`;

                  return (
                    <tr
                      key={`row-${hourIndex}`}
                      className={
                        hourIndex % 2 === 0
                          ? "bg-gray-800/60 border-t border-gray-700"
                          : "bg-gray-900 border-t border-gray-700"
                      }
                      style={{
                        color: "#e5e7eb",
                        fontSize: "11px",
                      }}
                    >
                      <td className="p-2 text-gray-400 border border-gray-700 font-semibold">
                        {timeLabel}
                      </td>

                      {daysOfWeek.map((day, dayIndex) => {
                        const date = format(
                          addDays(weekStart, dayIndex),
                          "yyyy-MM-dd"
                        );
                        const shifts = assignments.filter((a) => {
                          if (a.date !== date || !a.shift) return false;
                          const [start] = a.shift
                            .split("-")
                            .map((t) => t.trim());
                          const [startHour] = start
                            .split(":")
                            .map(Number);
                          return startHour === hour;
                        });

                        return (
                          <td
                            key={`cell-${hourIndex}-${dayIndex}`}
                            className="p-1 border border-gray-700 cursor-pointer hover:bg-gray-700/50"
                            onClick={() => setSelectedDay(date)}
                          >
                            <div className="flex justify-center flex-wrap gap-1">
                              {shifts.map((shift, sIndex) => (
                                <span
                                  key={`dot-${dayIndex}-${sIndex}`}
                                  style={{
                                    backgroundColor: getStaffColor(
                                      shift.staffName
                                    ),
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    display: "inline-block",
                                  }}
                                  title={`${shift.clientName} - ${shift.staffName}`}
                                ></span>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr
                  style={{ fontSize: "11px" }}
                  className="bg-gray-800 border-t border-gray-700 font-semibold"
                >
                  <td className="p-2 text-gray-300 border border-gray-700">
                    Total Hours
                  </td>
                  {daysOfWeek.map((day, i) => {
                    const date = format(
                      addDays(weekStart, i),
                      "yyyy-MM-dd"
                    );
                    const total = assignments
                      .filter((a) => a.date === date)
                      .reduce(
                        (sum, a) => sum + (a.totalHours || 0),
                        0
                      );
                    return (
                      <td
                        key={`total-${i}`}
                        className="p-2 text-gray-200 border border-gray-700"
                      >
                        {total > 0 ? total : 0}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Selected Day Details */}
          {selectedDay && (
            <div className="mt-6 bg-gray-800 rounded-xl p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-200">
                  Details for{" "}
                  {format(
                    new Date(selectedDay),
                    "EEE dd MMM yyyy"
                  )}
                </h3>
                <button
                  onClick={() => setSelectedDay(null)}
                  className="text-sm text-gray-400 hover:text-gray-200"
                >
                  ✖ Close
                </button>
              </div>

              <div className="space-y-2">
                {assignments.filter((a) => a.date === selectedDay)
                  .length === 0 ? (
                  <p className="text-gray-400 italic text-sm">
                    No shifts scheduled for this day.
                  </p>
                ) : (
                  assignments
                    .filter((a) => a.date === selectedDay)
                    .map((a, i) => (
                      <div
                        key={i}
                        style={{
                          backgroundColor: getStaffColor(a.staffName),
                          color: "white",
                          borderRadius: "8px",
                          padding: "8px",
                          margin: "10px",
                        }}
                        className="shadow-sm"
                      >
                        <p className="font-semibold text-sm">
                          {a.clientName}
                        </p>
                        <p className="text-xs italic">
                          {a.staffName}
                        </p>
                        <p className="text-xs">
                          {a.shift} ({a.totalHours}h)
                        </p>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* ✅ Toast Notification */}
        {toast.show && (
          <div
            style={{
              position: "fixed",
              bottom: "30px",
              right: "30px",
              backgroundColor:
                toast.type === "success" ? "#10b981" : "#ef4444",
              color: "white",
              padding: "12px 20px",
              borderRadius: "8px",
              boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              fontSize: "14px",
              fontWeight: "500",
              zIndex: 9999,
              transition: "all 0.3s ease",
            }}
          >
            {toast.message}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            color: "#9ca3af",
            marginTop: "40px",
            fontSize: "13px",
          }}
        >
          <p>
            © {new Date().getFullYear()} Convalesce Care & Recruitment
            Ltd.
            <br />
            <span style={{ color: "#d1d5db" }}>
              Reliable, Compliant, and Always Caring.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
